import type { DataSourceDataParams } from "@infinite-table/infinite-react";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import type { ResponseReadAll } from "epsm-api-types";
import { getParams } from "@/lib/utils";
import type { IReadAllParams } from "@/services/RequestParams";
import type { Options } from "./UseData";

const peadApi = {
	getData: async <T>(
		params: IReadAllParams,
		execute: (options: Readonly<Options>) => Promise<ResponseReadAll<T> | null>,
		signal?: AbortSignal,
	) => {
		const result = await execute({
			params,
			signal,
		});

		return {
			data: result?.data || [],
			hasMore: result?.metadata?.pagination?.nextPage,
		};
	},

	getQueryOptions: function <T>(
		tileId: string,
		ticker: string,
		dataParams: Partial<DataSourceDataParams<T>>,
		execute: (options: Readonly<Options>) => Promise<ResponseReadAll<T> | null>,
		priceResponseFields?: (keyof T)[],
	) {
		return queryOptions({
			queryKey: [
				tileId,
				ticker,
				dataParams.groupBy,
				dataParams.sortInfo,
				dataParams.filterValue,
			],
			queryFn: async ({ signal }) => {
				if (!dataParams.sortInfo) return { data: [] };

				const params: DataSourceDataParams<T> = {
					originalDataArray: [],
					groupBy: dataParams.groupBy,
					sortInfo: dataParams.sortInfo,
					filterValue: dataParams.filterValue,
				};

				let preparedParams = getParams<T>(
					{ ...params },
					priceResponseFields as string[],
				);

				preparedParams = {
					...preparedParams,
					types: {
						date: "date",
						"avg.date": "date",
						"max.date": "date",
						"min.date": "date",
						"sigma1.date": "date",
						"sigma_minus1.date": "date",
						"sigma2.date": "date",
						"sigma_minus2.date": "date",
					},
				};

				return await this.getData(preparedParams, execute, signal);
			},
			select: (res) => res?.data || [],

			placeholderData: keepPreviousData,
		});
	},
};

export default peadApi;
